import * as React from 'react';

import { useEvent } from '@/use-event';

interface IUseControllableStateProps<T> {
	prop?: T;
	defaultProp?: T;
	onChange?: (state: T) => void;
}

export const useControllableState = <T,>(props: IUseControllableStateProps<T>) => {
	const { defaultProp, onChange, prop } = props;
	const [uncontrolledProp, setUncontrolledProp] = React.useState<T | undefined>(defaultProp);
	const prevUncontrolledPropRef = React.useRef<T | undefined>(uncontrolledProp);

	const isControlled = prop !== undefined;
	const value = isControlled ? prop : uncontrolledProp;

	const handleChange = useEvent(onChange);

	const setValue = React.useCallback(
		(nextValue: React.SetStateAction<T>) => {
			if (isControlled) {
				const stateUpdateFn = nextValue as (prevState?: T) => T;
				const value = typeof stateUpdateFn === 'function' ? stateUpdateFn(prop) : nextValue;
				if (value !== prop) {
					handleChange(value as T);
				}
			} else {
				setUncontrolledProp(nextValue as T);
			}
		},
		[isControlled, value, handleChange],
	);

	React.useEffect(() => {
		if (prevUncontrolledPropRef.current !== uncontrolledProp) {
			handleChange(uncontrolledProp as T);
			prevUncontrolledPropRef.current = uncontrolledProp;
		}
	}, [handleChange, prevUncontrolledPropRef, uncontrolledProp]);

	return [value, setValue] as const;
};
